(() => {
  if (window.__vrGrowthConversionLoaded) return;
  window.__vrGrowthConversionLoaded = true;

  const root = document.querySelector('[data-vr-growth]');
  const panel = root?.querySelector('[data-vr-consent]');
  const dialog = root?.querySelector('.vr-consent__dialog');
  const form = root?.querySelector('[data-vr-consent-form]');
  const email = root?.querySelector('[data-vr-consent-email]');
  const tags = root?.querySelector('[data-vr-consent-tags]');
  const options = [...(root?.querySelectorAll('[data-vr-consent-option]') || [])];
  const dismissButtons = [...(root?.querySelectorAll('[data-vr-consent-dismiss]') || [])];
  const success = root?.querySelector('[data-vr-consent-success]');
  if (!root || !panel || !dialog) return;

  const storageKey = 'vrConsentPreferencesV1';
  const transitionDuration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 260;
  let returnFocus = null;
  let closeTimer = null;
  const track = (event) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event });
  };

  const savePreference = (choice, selected = []) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ choice, selected, savedAt: Date.now() }));
    } catch {}
  };

  const hasSavedPreference = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (!saved?.choice) return false;
      if (saved.choice === 'subscribed' || saved.choice === 'submitted') return true;
      return Date.now() - Number(saved.savedAt || 0) < 2592000000;
    } catch {
      return false;
    }
  };

  const focusable = () =>
    [...dialog.querySelectorAll('button:not([disabled]),input:not([disabled]),a[href]')].filter(
      (element) => element instanceof HTMLElement && element.offsetParent !== null
    );

  const cleanSubmissionUrl = () => {
    if (!window.history?.replaceState) return;
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('customer_posted');
    if (cleanUrl.hash.startsWith('#contact_')) cleanUrl.hash = '';
    window.history.replaceState(window.history.state, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
  };

  const open = (automatic = false) => {
    if (!panel.hidden) return;
    if (closeTimer) window.clearTimeout(closeTimer);
    returnFocus = document.activeElement;
    panel.hidden = false;
    panel.classList.remove('is-closing');
    document.body.classList.add('vr-consent-open');
    window.requestAnimationFrame(() => {
      panel.classList.add('is-open');
      const result = panel.querySelector('[data-vr-consent-success],[data-vr-consent-error]');
      (result || email || dialog).focus();
    });
    track(automatic ? 'vr_consent_auto_open' : 'vr_consent_open');
  };

  const close = (choice = 'dismissed') => {
    if (panel.hidden) return;
    if (closeTimer) window.clearTimeout(closeTimer);
    panel.classList.remove('is-open');
    panel.classList.add('is-closing');
    document.body.classList.remove('vr-consent-open');
    if (choice) savePreference(choice);
    closeTimer = window.setTimeout(() => {
      panel.hidden = true;
      panel.classList.remove('is-closing');
      if (choice === 'subscribed') cleanSubmissionUrl();
      if (returnFocus instanceof HTMLElement) returnFocus.focus();
    }, transitionDuration);
    track(choice === 'subscribed' ? 'vr_consent_complete' : 'vr_consent_declined');
  };

  dismissButtons.forEach((button) => button.addEventListener('click', () => close('declined')));

  document.addEventListener('keydown', (event) => {
    if (panel.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close('dismissed');
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = focusable();
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const phone = root?.querySelector('[data-vr-consent-phone]');

  form?.addEventListener('submit', (event) => {
    if (phone && !phone.reportValidity()) {
      event.preventDefault();
      return;
    }
    if (email && !email.reportValidity()) {
      event.preventDefault();
      return;
    }

    const selected = options.filter((option) => option.checked).map((option) => option.value);
    const preferenceTags = ['newsletter'];
    if (selected.includes('ritual-guidance')) preferenceTags.push('ritual-guidance');
    if (selected.includes('founders-research')) preferenceTags.push('founders-circle-research');
    if (tags) tags.value = preferenceTags.join(',');
    savePreference('submitted', selected);
    track('vr_email_optin_submit');
  });

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!reduceMotion.matches && !navigator.connection?.saveData) {
    let glassFrame = 0;
    const updateGlass = () => {
      const travel = ((window.scrollY % 320) / 320 - 0.5) * 22;
      document.documentElement.style.setProperty('--vr-glass-shift', `${travel.toFixed(2)}px`);
      glassFrame = 0;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!glassFrame) glassFrame = window.requestAnimationFrame(updateGlass);
      },
      { passive: true }
    );
    updateGlass();
  }

  const copyButton = root?.querySelector('[data-vr-consent-copy]');
  copyButton?.addEventListener('click', () => {
    const codeEl = root.querySelector('.vr-consent__code-text');
    const codeText = codeEl?.textContent?.trim();
    if (!codeText) return;
    navigator.clipboard?.writeText(codeText).then(() => {
      copyButton.textContent = 'Copied';
      window.setTimeout(() => { copyButton.textContent = 'Copy'; }, 1800);
    }).catch(() => {});
  });

  if (success) {
    panel.classList.add('has-success');
    dialog.classList.add('is-success');
    savePreference('subscribed', []);
    open();
    return;
  }
  if (root.querySelector('[data-vr-consent-error]')) {
    open();
    return;
  }
  if (hasSavedPreference() || window.Shopify?.designMode) return;

  // Do not replace the page's LCP candidate with a timed modal during initial load.
  // Open only after the visitor has genuinely engaged by scrolling into the page.
  const scrollableDistance = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  if (scrollableDistance > 0) {
    const engagementThreshold = Math.min(480, window.innerHeight * 0.55, scrollableDistance * 0.2);
    let engagementTimer = null;
    const openAfterEngagement = () => {
      if (window.scrollY < engagementThreshold || engagementTimer) return;
      window.removeEventListener('scroll', openAfterEngagement);
      engagementTimer = window.setTimeout(() => {
        if (panel.hidden && document.visibilityState === 'visible') open(true);
      }, 1200);
    };
    window.addEventListener('scroll', openAfterEngagement, { passive: true });
  }
})();
